import { fetchAuthSession } from 'aws-amplify/auth';
import { useKorintConfig } from 'korint-sdk/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { folioSpeechToTextCredCache, type FolioSpeechToTextState } from './speech-to-text-context';
import type { SpeechToTextCredentials } from './credentialsCache';
import { loadSpeechToTextProvider } from './providers/speech-to-text-provider';

export type SpeechToTextErrorReason =
  | 'permission_denied'
  | 'network_error'
  | 'max_duration_reached'
  | 'unsupported_browser'
  | 'unauthorized';

export interface FolioDictation {
  state: FolioSpeechToTextState;
  partial: string;
  error?: SpeechToTextErrorReason;
  start: () => void;
  stop: () => void;
}

interface UseDictationParams {
  onFinalSegment: (text: string) => void;
  preferredLanguage?: 'fr-FR' | 'en-US';
  productId?: string;
}

const MAX_STREAM_DURATION_MS = 5 * 60 * 1000;

interface AmplifyTokenSource {
  tokens?: { accessToken?: { toString: () => string } };
}

function buildCredentialsFetcher(opts: { apiBaseUrl: string; tenantId: string }) {
  return async (productId?: string): Promise<SpeechToTextCredentials> => {
    let token: string | undefined;
    try {
      const session = (await fetchAuthSession()) as AmplifyTokenSource;
      token = session.tokens?.accessToken?.toString();
    } catch {
      // No session — gateway returns 401/403 and we surface unauthorized.
    }
    const res = await fetch(`${opts.apiBaseUrl}/speech-to-text/credentials`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        TENANT: opts.tenantId,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(productId ? { productId } : {}),
    });
    if (res.status === 401 || res.status === 403) throw new Error('unauthorized');
    if (!res.ok) throw new Error('network_error');
    return res.json();
  };
}

export function useDictation(params: UseDictationParams): FolioDictation {
  const { clientOptions } = useKorintConfig();
  const apiBaseUrl = clientOptions.baseUrl;
  const tenantId = clientOptions.tenant;

  const [state, setState] = useState<FolioSpeechToTextState>('idle');
  const [partial, setPartial] = useState('');
  const [error, setError] = useState<SpeechToTextErrorReason | undefined>();
  const captureRef = useRef<{ stop: () => void } | undefined>(undefined);
  const timerRef = useRef<number | undefined>(undefined);
  const partialRef = useRef('');
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const stop = useCallback(() => {
    if (partialRef.current) {
      paramsRef.current.onFinalSegment(partialRef.current);
      partialRef.current = '';
    }
    captureRef.current?.stop();
    captureRef.current = undefined;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = undefined;
    setPartial('');
    setState('idle');
  }, []);

  const start = useCallback(async () => {
    if (!apiBaseUrl || !tenantId) {
      setError('network_error');
      setState('error');
      return;
    }
    setState('requesting');
    setError(undefined);
    try {
      const fetcher = buildCredentialsFetcher({ apiBaseUrl, tenantId });
      const [{ startAudioCapture }, credentials] = await Promise.all([
        import('./audioCapture'),
        folioSpeechToTextCredCache.get(params.productId, fetcher),
      ]);
      const { transcribe } = await loadSpeechToTextProvider(credentials.provider);

      const capture = await startAudioCapture({ onSilence: () => stop() });
      captureRef.current = capture;
      setState('recording');

      timerRef.current = window.setTimeout(() => {
        setError('max_duration_reached');
        stop();
      }, MAX_STREAM_DURATION_MS);

      (async () => {
        try {
          for await (const seg of transcribe({
            frames: capture.frames,
            credentials,
            preferredLanguage: params.preferredLanguage,
          })) {
            if (seg.isPartial) {
              partialRef.current = seg.text;
              setPartial(seg.text);
            } else {
              partialRef.current = '';
              setPartial('');
              params.onFinalSegment(seg.text);
            }
          }
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : '';
          if (/NotAuthorized|AccessDenied|ExpiredToken|InvalidSignature/i.test(msg)) {
            setError('unauthorized');
          } else {
            setError('network_error');
          }
          setState('error');
        } finally {
          if (captureRef.current) stop();
        }
      })();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '';
      if (msg === 'unauthorized') setError('unauthorized');
      else if (msg.includes('Permission') || msg.includes('NotAllowed')) {
        setError('permission_denied');
      } else {
        setError('network_error');
      }
      setState('error');
    }
  }, [apiBaseUrl, tenantId, params, stop]);

  useEffect(() => () => stop(), [stop]);

  return { state, partial, error, start, stop };
}
